import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { modelManager } from '@/editor/manager/entityManager';
import { globalStores } from '@/editor/stores';
import { EOperateMode, EToolType } from '@/editor/types';
import { cloneDeep } from 'lodash-es';
import { EAutoLayoutMode, getLayoutableRoomList, getPartialLayoutInfo } from './detect';

/** 进入布局模式前各家具的可见状态，退出时据此还原 */
const furnitureVisibleSnapshot = new Map<string, boolean>();

type TAutoLayoutData = typeof globalStores.editorStateStore.autoLayoutModeData;

/** 进入 apply 模式前的可交互区域，退出 apply 时据此还原 */
let autoLayoutDataSnapshot: TAutoLayoutData | null = null;

/** 每次都要新的数组：三个 id 列表会被调用方整体赋值替换，共享同一份字面量会串场 */
function createEmptyAutoLayoutData(): TAutoLayoutData {
  return { allowedRoomIds: [], disabledRoomIds: [], allowedDoorIds: [] };
}

export function focusLayoutRegion(id?: string, relateRoomId?: string) {
  const { regionManager, textInfoManager, roomManager } = modelManager;
  const isFullMode = globalStores.editorStateStore.autoLayoutMode === EAutoLayoutMode.FULL;

  if (isFullMode) {
    if (!id) {
      roomManager.entities.forEach((entity) => {
        entity.changeFloorPlaneStatus(false);
      });

      regionManager.entities.forEach((entity) => {
        entity.changeFloorPlaneStatus(false);
      });
      textInfoManager.setFocus({
        type: 'room',
        id: ''
      });
    } else {
      roomManager.entities.forEach((entity) => {
        if (entity.id !== id) {
          entity.changeFloorPlaneStatus(false);
        } else if (id) {
          entity.changeFloorPlaneStatus(true);
          regionManager.entities.forEach((entity) => {
            entity.changeFloorPlaneStatus(entity.regionData.relateRoomId === id);
          });
          textInfoManager.setFocus({
            type: 'room',
            id
          });
        }
      });
    }
  } else {
    if (!id) {
      regionManager.entities.forEach((entity) => {
        entity.changeFloorPlaneStatus(false);
      });
      textInfoManager.setFocus({
        type: 'region',
        id: ''
      });
    } else {
      regionManager.entities.forEach((entity) => {
        if (entity.id !== id) {
          entity.changeFloorPlaneStatus(false);
        } else if (id) {
          entity.changeFloorPlaneStatus(true);
          textInfoManager.setFocus({
            type: 'region',
            id
          });
        }
      });

      roomManager.entities.forEach((entity) => {
        entity.changeFloorPlaneStatus(entity.id === relateRoomId);
      });
    }
  }
}

export function focusLayoutDoor(id: string) {
  modelManager.indicatorManager.setDoorIndicator(id);
}

/** apply 模式下结果已生成，禁掉选中与 hover 避免用户改动已参与计算的区域 */
export function setLayoutApplyMode(enable: boolean) {
  if (enable) {
    autoLayoutDataSnapshot = cloneDeep(globalStores.editorStateStore.autoLayoutModeData);
    globalStores.editorStateStore.autoLayoutModeData = createEmptyAutoLayoutData();
  } else if (autoLayoutDataSnapshot) {
    globalStores.editorStateStore.autoLayoutModeData = autoLayoutDataSnapshot;
    autoLayoutDataSnapshot = null;
  }

  modelManager.indicatorManager.setVisible(!enable);
}

function restoreAllRegion() {
  const { regionManager, textInfoManager, roomManager } = modelManager;

  regionManager.entities.forEach((entity) => {
    entity.changeFloorPlaneStatus(true);
  });
  roomManager.entities.forEach((entity) => {
    entity.changeFloorPlaneStatus(true);
  });
  textInfoManager.resetFocus();
}

/** 幂等：同一时刻只允许存在一个布局会话，重复进入会覆盖掉家具可见状态快照 */
function enterAutoLayoutMode(mode: EAutoLayoutMode, regionId?: string) {
  if (globalStores.editorStateStore.autoLayoutMode !== EAutoLayoutMode.NONE) return;

  const { furnitureManager, indicatorManager, dimensionManager } = modelManager;

  globalStores.operateInfo = {
    mode: EOperateMode.Layout,
    toolType: EToolType.empty
  };

  // 记录当前家具的可见状态并隐藏家具
  if (mode === EAutoLayoutMode.FULL) {
    furnitureManager.entities.forEach((furniture) => {
      furnitureVisibleSnapshot.set(furniture.id, furniture.visible);
      furniture.setVisible(false);
    });
    furnitureManager.update();
  }

  // 清除画布的选中状态和 hover 状态
  globalStores.editorStateStore.clearSelected();
  globalStores.editorStateStore.clearHoverEntity();

  // 进入布局模式
  globalStores.editorStateStore.setAutoLayoutMode(mode);

  // dimensionManager 没有可订阅的 update 事件，只能显式刷，且必须排在 setAutoLayoutMode 之后——它读的就是 autoLayoutMode
  // 墙体标尺不用管：operateInfo 切成 Layout 时 rulerManager 已经同步清掉了
  dimensionManager.update();

  // 计算可用于自动布局的区域
  if (mode === EAutoLayoutMode.FULL) {
    const roomList = getLayoutableRoomList(false);
    globalStores.editorStateStore.autoLayoutModeData.allowedRoomIds = roomList
      .filter((room) => room.isValidRoom)
      .map((room) => room.roomData.id);
    globalStores.editorStateStore.autoLayoutModeData.disabledRoomIds = roomList
      .filter((room) => !room.isValidRoom)
      .map((room) => room.roomData.id);
    indicatorManager.setVisible(true);
  } else {
    const data = getPartialLayoutInfo(regionId);
    if (data) {
      focusLayoutRegion(data.region.id, data.region.relateRoomId);
      globalStores.editorStateStore.autoLayoutModeData = createEmptyAutoLayoutData();
    }
  }
}

/** 幂等：兜底调用与 React 严格模式下的重复卸载都会走到这里 */
function exitAutoLayoutMode() {
  if (globalStores.editorStateStore.autoLayoutMode === EAutoLayoutMode.NONE) return;

  const { furnitureManager, indicatorManager, dimensionManager } = modelManager;

  // 恢复之前记录的家具可见状态。局部布局没隐藏过家具，快照为空时不必惊动 manager
  if (furnitureVisibleSnapshot.size > 0) {
    furnitureManager.entities.forEach((furniture) => {
      const prevVisible = furnitureVisibleSnapshot.get(furniture.id);
      if (prevVisible !== undefined) {
        furniture.setVisible(prevVisible);
      }
    });
    furnitureVisibleSnapshot.clear();
    furnitureManager.update();
  }

  autoLayoutDataSnapshot = null;

  globalStores.operateInfo = {
    mode: EOperateMode.EMPTY,
    toolType: EToolType.empty
  };

  // 清除画布的选中状态和 hover 状态
  globalStores.editorStateStore.clearSelected();
  globalStores.editorStateStore.clearHoverEntity();

  // 退出布局模式
  globalStores.editorStateStore.setAutoLayoutMode(EAutoLayoutMode.NONE);

  // 手绘尺寸标注按用户自己的隐藏集重新渲染。整个布局模式期间都没碰过那个集合，
  // 所以进来时是「部分隐藏」，出去还是「部分隐藏」
  dimensionManager.update();

  // 恢复所有区域的显示状态
  restoreAllRegion();

  indicatorManager.setVisible(false);
  indicatorManager.setDoorIndicator('');
}

/**
 * 全屋布局的唯一入口。不传 roomId 表示由界面自行挑选聚焦房间（导航栏入口）。
 * @param roomId 指定聚焦的房间
 */
export function startFullLayout(roomId?: string) {
  // 局部布局中点导航栏会走到这里。不先收掉上一个会话的话，enterAutoLayoutMode 会被自身的
  // 幂等判断挡下，内核留在旧模式，而界面已经按新的类型切过去了
  endLayout();
  enterAutoLayoutMode(EAutoLayoutMode.FULL);
  editorEvent.emit(EditorEventName.LAYOUT_START, { id: roomId, type: 'full' });
}

/** 局部布局的唯一入口。同 startFullLayout，先收掉可能存在的上一个会话 */
export function startPartialLayout(regionId: string) {
  endLayout();
  enterAutoLayoutMode(EAutoLayoutMode.PARTIAL, regionId);
  editorEvent.emit(EditorEventName.LAYOUT_START, { id: regionId, type: 'partial' });
}

/** 退出布局的唯一入口，全屋与局部共用 */
export function endLayout() {
  if (globalStores.editorStateStore.autoLayoutMode === EAutoLayoutMode.NONE) return;

  exitAutoLayoutMode();
  editorEvent.emit(EditorEventName.LAYOUT_END);
}
